import { Box, Button, Card, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';

import Avatar from '@/components/common/Avatar';
import type { EventResponse } from '@/types';

interface DesktopMentionItemProps {
  event: EventResponse;
  dateDay: string;
  dateMonth: string;
  dateTime: string;
  options: string[];
  moreOptionsCount: number;
  formattedVolume: string;
  onTradeClick: (eventId: string) => void;
}

export const DesktopMentionItem = ({
  event,
  dateDay,
  dateMonth,
  dateTime,
  options,
  moreOptionsCount,
  formattedVolume,
  onTradeClick,
}: DesktopMentionItemProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClick = () => {
    onTradeClick(event.id);
  };

  const renderOption = (option: string, index: number) => (
    <Chip
      key={index}
      label={option}
      sx={{
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        fontSize: '0.8rem',
        fontWeight: 500,
        height: '28px',
        color: 'text.secondary',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    />
  );

  const renderedOptions = options.map(renderOption);

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, sm: 3 },
        p: { xs: 2, sm: 3 },
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.4)'
              : '0 4px 12px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Date Box */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: { xs: 50, sm: 60 },
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1,
          }}
        >
          {dateDay}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {dateMonth}
        </Typography>
      </Box>

      {/* Event Icon */}
      <Avatar
        src={event?.icon || event?.image}
        alt={event?.title || 'Event'}
        size={isMobile ? 60 : 80}
        sx={{
          borderRadius: '8px',
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          fontSize: { xs: '2rem', sm: '2.5rem' },
        }}
      />

      {/* Event Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          onClick={handleClick}
          sx={{
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
            lineHeight: 1.4,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {event?.title || 'Untitled Event'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
          {dateTime && (
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {dateTime}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {formattedVolume} Vol.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {renderedOptions}
        {moreOptionsCount > 0 && (
          <Typography
            sx={{ fontSize: '0.75rem', color: 'text.secondary', alignSelf: 'center', ml: 1 }}
          >
            <Chip
              label={`+${moreOptionsCount} more`}
              sx={{
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 500,
                height: '28px',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            />
          </Typography>
        )}
      </Box>

      {/* Trade Button */}
      <Button
        variant="contained"
        onClick={handleClick}
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontWeight: 600,
          fontSize: '0.9rem',
          px: 3,
          py: 1,
          borderRadius: '8px',
          textTransform: 'none',
          minWidth: 100,
          flexShrink: 0,
          '&:hover': {
            bgcolor: 'primary.dark',
          },
        }}
      >
        Trade
      </Button>
    </Card>
  );
};
